import { v1, toResult } from '@/infrastructure/api/v1/client';

/**
 * Session-scoped cache + in-flight dedupe for the `userId → org_members.id`
 * lookup. Adapters (Library, UIElement, etc.) hit the same single-row query
 * many times during a session — caching it once avoids burst traffic on
 * `org_members` and prevents 429s when several callers fan out in parallel.
 *
 * Only positive (non-null) results are cached so that newly-onboarded
 * accounts can recover without a page refresh.
 */
const cache = new Map<string, number>();
const inflight = new Map<string, Promise<number | null>>();

/** The signed-in user's membership id; `userId` keys the cache and must be that user's id. */
export async function getOrgMemberId(userId: string): Promise<number | null> {
  const cached = cache.get(userId);
  if (cached !== undefined) {
    return cached;
  }

  const existing = inflight.get(userId);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<number | null> => {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/orgs/me/member'));
      if (error) {
        return null;
      }

      const id = data.id;
      cache.set(userId, id);
      return id;
    } finally {
      inflight.delete(userId);
    }
  })();

  inflight.set(userId, promise);
  return promise;
}

/**
 * Drop a cached entry (or the whole cache). Call on logout / user switch
 * if the page is not being reloaded.
 */
export function invalidateOrgMemberId(userId?: string): void {
  if (userId) {
    cache.delete(userId);
  } else {
    cache.clear();
  }
}
